import { useEffect, useRef, useState, type RefObject } from "react";
import {
  ArrowLeft,
  CalendarHeart,
  ChartNoAxesColumn,
  Download,
  RefreshCw,
  Trash2,
} from "lucide-react";
import duckChartGif from "../../../assets/utya-chart.gif";
import { formatRub } from "../../../lib/format";
import { DateField } from "../../../ui/DateField";
import { StatsLoadingCards } from "../../../ui/StatsLoadingCards";
import { StatsSnapshotTime } from "../../../ui/StatsSnapshotTime";
import { driverApi, type DriverStatsSnapshot as Stats } from "../lib/api";

type Props = {
  flowId?: number;
  flowName?: string;
  onBack?: () => void;
};

const STATS_REFRESH_MS = 300_000;
const DRIVER_STATS_LOADING_ROWS = [4, 3] as const;
const RESOURCE_STATS_LOADING_ROWS = [3] as const;
const STATS_UNAVAILABLE = "статистика временно недоступна";

export function DriverStats({ flowId, flowName, onBack }: Props) {
  const [initialDate] = useState(todayIso);
  const [stats, setStats] = useState<Stats | null>(null);
  const [dateFrom, setDateFrom] = useState(initialDate);
  const [dateTo, setDateTo] = useState(initialDate);
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: initialDate,
    dateTo: initialDate,
  });
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resourceMode, setResourceMode] = useState(false);
  const [resourceDate, setResourceDate] = useState(initialDate);
  const [resourceStats, setResourceStats] = useState<Stats | null>(null);
  const [resourceError, setResourceError] = useState<string | null>(null);
  const [resourceLoading, setResourceLoading] = useState(false);
  const statsRequestId = useRef(0);
  const statsRequestInFlight = useRef<Promise<boolean> | null>(null);
  const latestSnapshotByKey = useRef(new Map<string, Map<string, number>>());
  const resourceStatsRequestId = useRef(0);
  const reloadMainOnClose = useRef(false);
  const statsHeadingRef = useRef<HTMLHeadingElement>(null);
  const restoreStatsHeadingFocus = useRef(false);

  async function load(
    nextDateFrom = dateFrom,
    nextDateTo = dateTo,
    options: {
      showLoading?: boolean;
      silent?: boolean;
      focusHeading?: boolean;
      refresh?: boolean;
    } = {},
  ) {
    const showLoading = options.showLoading ?? true;
    const silent = options.silent ?? false;
    const focusHeading = options.focusHeading ?? false;
    const refresh = options.refresh ?? false;
    const isBackgroundRefresh = silent && !showLoading;
    const pendingRequest = statsRequestInFlight.current;
    if (isBackgroundRefresh && pendingRequest) return false;

    const requestId = ++statsRequestId.current;
    if (showLoading) {
      if (focusHeading) restoreStatsHeadingFocus.current = true;
      setLoading(true);
    }
    if (pendingRequest) {
      await pendingRequest;
      if (requestId !== statsRequestId.current) return false;
    }

    const request = (async () => {
      try {
        const r = await driverApi.getStats({
          flowId,
          dateFrom: nextDateFrom,
          dateTo: nextDateTo,
          ...(refresh ? { refresh: true } : {}),
        });
        if (requestId !== statsRequestId.current) return false;
        if (r.status === 503) {
          setError(STATS_UNAVAILABLE);
          setStats(null);
          setAppliedFilters({ dateFrom: nextDateFrom, dateTo: nextDateTo });
          return false;
        }
        if ((r.status !== undefined && r.status >= 400) || r.error || !r.data) {
          setError("не удалось загрузить статистику");
          setStats(null);
          setAppliedFilters({ dateFrom: nextDateFrom, dateTo: nextDateTo });
          return false;
        }
        const nextStats = r.data;
        const snapshotKey = [flowId ?? "", nextDateFrom, nextDateTo].join("|");
        if (
          !acceptSnapshot(
            latestSnapshotByKey.current,
            snapshotKey,
            nextStats.snapshot_generated_at,
            nextStats.snapshot_source,
          )
        ) {
          setError("не удалось загрузить статистику");
          setStats(null);
          setAppliedFilters({ dateFrom: nextDateFrom, dateTo: nextDateTo });
          return false;
        }
        setStats(nextStats);
        setAppliedFilters({ dateFrom: nextDateFrom, dateTo: nextDateTo });
        setError(null);
        return true;
      } catch {
        if (requestId === statsRequestId.current) {
          setError("не удалось загрузить статистику");
          setStats(null);
          setAppliedFilters({ dateFrom: nextDateFrom, dateTo: nextDateTo });
        }
        return false;
      }
    })();
    statsRequestInFlight.current = request;

    try {
      return await request;
    } finally {
      if (statsRequestInFlight.current === request) {
        statsRequestInFlight.current = null;
      }
      if (requestId === statsRequestId.current) {
        if (showLoading) setLoading(false);
      }
    }
  }

  useEffect(() => {
    const defaults = defaultDateRange();
    setDateFrom(defaults.dateFrom);
    setDateTo(defaults.dateTo);
    setAppliedFilters(defaults);
    setResourceDate(defaults.dateFrom);
    resourceStatsRequestId.current += 1;
    setResourceMode(false);
    setResourceStats(null);
    setResourceError(null);
    reloadMainOnClose.current = false;
    setResourceLoading(false);
    void load(defaults.dateFrom, defaults.dateTo, { showLoading: true });
  }, [flowId]);

  useEffect(() => {
    let active = true;
    let refreshTimer: number | undefined;
    let refreshing = false;
    let refreshOnReturn = false;

    function scheduleRefresh() {
      if (
        !active ||
        document.hidden ||
        refreshing ||
        refreshTimer !== undefined
      )
        return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = undefined;
        void refreshAndSchedule();
      }, STATS_REFRESH_MS);
    }

    async function waitForCurrentRequest() {
      const request = statsRequestInFlight.current;
      if (request) await request;
    }

    async function refreshAndSchedule(refresh = false) {
      if (!active || document.hidden) return;
      if (refreshing) {
        refreshOnReturn ||= refresh;
        return;
      }
      refreshing = true;
      try {
        await waitForCurrentRequest();
        if (!active || document.hidden) return;
        const forceRefresh = refresh || refreshOnReturn;
        refreshOnReturn = false;
        if (resourceMode) {
          await loadResourceStats(resourceDate, {
            showLoading: false,
            silent: true,
            refresh: forceRefresh,
          });
        } else {
          await load(appliedFilters.dateFrom, appliedFilters.dateTo, {
            showLoading: false,
            silent: true,
            refresh: forceRefresh,
          });
        }
      } finally {
        refreshing = false;
        if (active && !document.hidden && refreshOnReturn) {
          refreshOnReturn = false;
          void refreshAndSchedule(true);
        } else {
          scheduleRefresh();
        }
      }
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        if (refreshing) refreshOnReturn = true;
        if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
        refreshTimer = undefined;
        return;
      }
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
      refreshTimer = undefined;
      void refreshAndSchedule(true);
    }

    function handleFocus() {
      if (!document.hidden) handleVisibilityChange();
    }

    void waitForCurrentRequest().then(scheduleRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      active = false;
      if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [
    flowId,
    appliedFilters.dateFrom,
    appliedFilters.dateTo,
    resourceMode,
    resourceDate,
  ]);

  useEffect(() => {
    if (!loading && !resourceLoading && restoreStatsHeadingFocus.current) {
      statsHeadingRef.current?.focus();
      restoreStatsHeadingFocus.current = false;
    }
  }, [loading, resourceLoading, stats, resourceStats, resourceMode]);

  async function exportCsv() {
    setExporting(true);
    setError(null);
    setMessage(null);
    try {
      const r = await driverApi.sendStatsExport({
        flowId,
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo,
      });
      if (r.error || !r.data) {
        setError("не удалось отправить выгрузку в бот");
        return;
      }
      setMessage("файл успешно отправлен в бота!");
    } catch {
      setError("не удалось отправить файл в бот");
    } finally {
      setExporting(false);
    }
  }

  async function loadResourceStats(
    nextDate = resourceDate,
    options: {
      refresh?: boolean;
      showLoading?: boolean;
      silent?: boolean;
    } = {},
  ) {
    if (!flowId) return false;
    const showLoading = options.showLoading ?? true;
    const silent = options.silent ?? false;
    const pendingRequest = statsRequestInFlight.current;
    if (silent && !showLoading && pendingRequest) return false;
    const requestId = ++resourceStatsRequestId.current;
    if (showLoading) {
      restoreStatsHeadingFocus.current = true;
      setResourceLoading(true);
    }
    if (!silent) {
      setMessage(null);
    }
    if (pendingRequest) {
      await pendingRequest;
      if (requestId !== resourceStatsRequestId.current) return false;
    }

    const request = (async () => {
      try {
        const r = await driverApi.getStats({
          flowId,
          dateFrom: nextDate,
          dateTo: nextDate,
          ...(options.refresh ? { refresh: true } : {}),
        });
        if (requestId !== resourceStatsRequestId.current) return false;
        if (r.status === 503) {
          setResourceError(STATS_UNAVAILABLE);
          setError(STATS_UNAVAILABLE);
          setResourceStats(null);
          setStats(null);
          reloadMainOnClose.current = true;
          return false;
        }
        if ((r.status !== undefined && r.status >= 400) || r.error || !r.data) {
          setResourceError("не удалось загрузить статистику ресурсов");
          setError("не удалось загрузить статистику ресурсов");
          setResourceStats(null);
          setStats(null);
          reloadMainOnClose.current = true;
          return false;
        }
        const nextStats = r.data;
        const snapshotKey = [flowId, nextDate, nextDate].join("|");
        if (
          !acceptSnapshot(
            latestSnapshotByKey.current,
            snapshotKey,
            nextStats.snapshot_generated_at,
            nextStats.snapshot_source,
          )
        ) {
          setResourceError("не удалось загрузить статистику ресурсов");
          setError("не удалось загрузить статистику ресурсов");
          setResourceStats(null);
          setStats(null);
          reloadMainOnClose.current = true;
          return false;
        }
        setResourceStats(nextStats);
        setResourceError(null);
        return true;
      } catch {
        if (requestId !== resourceStatsRequestId.current) return false;
        setResourceError("не удалось загрузить статистику ресурсов");
        setError("не удалось загрузить статистику ресурсов");
        setResourceStats(null);
        setStats(null);
        reloadMainOnClose.current = true;
        return false;
      }
    })();
    statsRequestInFlight.current = request;

    try {
      return await request;
    } finally {
      if (statsRequestInFlight.current === request) {
        statsRequestInFlight.current = null;
      }
      if (showLoading && requestId === resourceStatsRequestId.current) {
        setResourceLoading(false);
      }
    }
  }

  function closeResourceStats() {
    const reloadMainStats = reloadMainOnClose.current;
    reloadMainOnClose.current = false;
    resourceStatsRequestId.current += 1;
    setResourceLoading(false);
    setResourceMode(false);
    setResourceError(null);
    if (reloadMainStats) {
      void load(appliedFilters.dateFrom, appliedFilters.dateTo, {
        showLoading: true,
      });
    }
  }

  if (!resourceMode && (loading || (!stats && !error))) {
    return (
      <DriverStatsLoadingScreen
        cardRows={DRIVER_STATS_LOADING_ROWS}
        focusHeading={restoreStatsHeadingFocus.current}
        headingRef={statsHeadingRef}
        onBack={onBack}
      />
    );
  }

  if (resourceMode && flowId && resourceLoading) {
    return (
      <DriverStatsLoadingScreen
        cardRows={RESOURCE_STATS_LOADING_ROWS}
        focusHeading
        headingRef={statsHeadingRef}
        onBack={closeResourceStats}
      />
    );
  }

  if (resourceMode && flowId) {
    return (
      <div className="tp-screen tp-driver-stats-screen tp-resource-stats-screen">
        <button
          className="tp-resource-stats-back"
          onClick={closeResourceStats}
          type="button"
          aria-label="назад"
        >
          ←
        </button>

        <div className="tp-driver-stats-hero">
          <img
            src={duckChartGif}
            alt=""
            aria-hidden="true"
            className="tp-driver-stats-duck"
            draggable={false}
          />
          <h1
            ref={statsHeadingRef}
            className="tp-driver-stats-title"
            tabIndex={-1}
          >
            статистика по ресурсам
          </h1>
        </div>

        <div className="tp-stats-filter-panel">
          <DateField
            value={resourceDate}
            ariaLabel="дата статистики ресурсов"
            onChange={(value) => {
              setResourceDate(value);
              void loadResourceStats(value, { refresh: true });
            }}
            icon={<CalendarHeart className="h-5 w-5" />}
          />
          <button
            className="tp-stats-filter-button"
            onClick={() => loadResourceStats(resourceDate, { refresh: true })}
            type="button"
          >
            <RefreshCw
              className="h-[1.125rem] w-[1.125rem]"
              aria-hidden="true"
            />
            <span>обновить</span>
          </button>
        </div>

        {resourceError ? <div className="tp-alert">{resourceError}</div> : null}
        {resourceStats ? (
          <>
            <StatsSnapshotTime
              generatedAt={resourceStats.snapshot_generated_at}
            />
            {resourceStats.snapshot_emergency_paused ? (
              <p role="status" className="tp-alert">
                аварийный режим статистики: данные могут быть устаревшими;
                проверьте время снимка
              </p>
            ) : null}
          </>
        ) : null}
        {!resourceError ? (
          <ResourceActionList resources={resourceStats?.resources ?? []} />
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={
        onBack
          ? "tp-screen tp-driver-stats-screen tp-resource-stats-screen"
          : "tp-screen tp-driver-stats-screen"
      }
    >
      {onBack ? (
        <button
          className="tp-resource-stats-back"
          onClick={onBack}
          type="button"
          aria-label="назад"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}
      <div className="tp-driver-stats-hero">
        <img
          src={duckChartGif}
          alt=""
          aria-hidden="true"
          className="tp-driver-stats-duck"
          draggable={false}
        />
        <h1
          ref={statsHeadingRef}
          className="tp-driver-stats-title"
          tabIndex={-1}
        >
          {flowId ? (flowName ?? `поток #${flowId}`) : "общая статистика"}
        </h1>
      </div>

      <DateFilters
        dateFrom={dateFrom}
        dateTo={dateTo}
        loading={loading}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        onApply={() =>
          load(dateFrom, dateTo, { focusHeading: true, refresh: true })
        }
        onReset={() => {
          const defaults = defaultDateRange();
          setDateFrom(defaults.dateFrom);
          setDateTo(defaults.dateTo);
          void load(defaults.dateFrom, defaults.dateTo, {
            focusHeading: true,
            refresh: true,
          });
        }}
      />

      {error ? <div className="tp-alert">{error}</div> : null}
      {message ? (
        <div className="tp-card-soft tp-success-message text-sm">{message}</div>
      ) : null}

      {stats ? (
        <>
          <StatsSnapshotTime generatedAt={stats.snapshot_generated_at} />
          {stats.snapshot_emergency_paused ? (
            <p role="status" className="tp-alert">
              аварийный режим статистики: данные могут быть устаревшими;
              проверьте время снимка
            </p>
          ) : null}
          <StatsContent
            stats={stats}
            exporting={exporting}
            onExport={exportCsv}
            onResourceStats={
              flowId
                ? () => {
                    setResourceMode(true);
                    setResourceStats(null);
                    setResourceError(null);
                    void loadResourceStats(resourceDate);
                  }
                : undefined
            }
          />
        </>
      ) : null}
    </div>
  );
}

function StatsSummaryBlocks({ stats }: { stats: Stats }) {
  const ownResources = stats.resources.filter((r) => r.owner_role === "driver");
  return (
    <>
      <section className="tp-stats-card" aria-label="основная статистика">
        <StatsRow
          label="запуски mini app"
          value={String(stats.summary.mini_app_clicks)}
        />
        <StatsRow
          label="подписчики"
          value={String(stats.summary.subscribers)}
        />
        <StatsRow
          label="заработано"
          value={`${formatRub(stats.summary.earned)} ₽`}
        />
        <StatsRow
          label="на проверке"
          value={`${formatRub(stats.summary.frozen)} ₽`}
        />
      </section>

      <section className="tp-stats-card" aria-label="мои ресурсы">
        <h2 className="tp-stats-section-title">мои ресурсы</h2>
        {ownResources.length === 0 ? (
          <StatsRow label="ресурсов нет" value="0" />
        ) : (
          ownResources.map((resource) => (
            <StatsRow
              key={resource.id}
              label={resource.public_name || resource.name}
              value={String(resource.subscribers)}
            />
          ))
        )}
      </section>
    </>
  );
}

function DriverStatsLoadingScreen({
  cardRows,
  focusHeading = false,
  headingRef,
  onBack,
}: {
  cardRows: readonly number[];
  focusHeading?: boolean;
  headingRef: RefObject<HTMLHeadingElement>;
  onBack?: () => void;
}) {
  useEffect(() => {
    if (focusHeading) headingRef.current?.focus();
  }, [focusHeading, headingRef]);

  return (
    <div
      className={
        onBack
          ? "tp-screen tp-driver-stats-screen tp-resource-stats-screen tp-stats-loading-screen"
          : "tp-screen tp-driver-stats-screen tp-stats-loading-screen"
      }
    >
      {onBack ? (
        <button
          className="tp-resource-stats-back"
          onClick={onBack}
          type="button"
          aria-label="назад"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}
      <div className="tp-driver-stats-hero">
        <img
          src={duckChartGif}
          alt=""
          aria-hidden="true"
          className="tp-driver-stats-duck"
          draggable={false}
        />
        <h1
          ref={headingRef}
          className="tp-driver-stats-title tp-stats-loading-title"
          tabIndex={focusHeading ? -1 : undefined}
        >
          загружаем статистику
        </h1>
      </div>
      <StatsLoadingCards cardRows={cardRows} />
    </div>
  );
}

function StatsContent({
  stats,
  exporting,
  onExport,
  onResourceStats,
}: {
  stats: Stats;
  exporting: boolean;
  onExport: () => void;
  onResourceStats?: () => void;
}) {
  return (
    <>
      <StatsSummaryBlocks stats={stats} />

      <div className="tp-stats-bottom-actions">
        <button
          className="tp-button-primary tp-stats-wide-button"
          disabled={exporting}
          onClick={onExport}
        >
          <Download className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
          <span>{exporting ? "отправляем..." : "получить выгрузку"}</span>
        </button>
        {onResourceStats ? (
          <button
            className="tp-stats-filter-button tp-stats-wide-button"
            onClick={onResourceStats}
            type="button"
          >
            <ChartNoAxesColumn
              className="h-[1.125rem] w-[1.125rem]"
              aria-hidden="true"
            />
            <span>статистика по ресурсам</span>
          </button>
        ) : null}
      </div>
    </>
  );
}

function ResourceActionList({ resources }: { resources: Stats["resources"] }) {
  if (resources.length === 0) {
    return (
      <section className="tp-stats-card" aria-label="статистика ресурсов">
        <StatsRow label="ресурсов нет" value="0" />
      </section>
    );
  }

  return (
    <section className="tp-stats-card" aria-label="статистика ресурсов">
      {resources.map((resource) => (
        <StatsRow
          key={resource.id}
          label={resource.public_name || resource.name}
          value={String(resource.subscribers)}
        />
      ))}
    </section>
  );
}

function StatsRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tp-stats-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function DateFilters({
  dateFrom,
  dateTo,
  loading,
  onDateFrom,
  onDateTo,
  onApply,
  onReset,
}: {
  dateFrom: string;
  dateTo: string;
  loading: boolean;
  onDateFrom: (value: string) => void;
  onDateTo: (value: string) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  return (
    <div className="tp-stats-filter-panel">
      <div className="tp-stats-date-row">
        <DateField
          value={dateFrom}
          ariaLabel="дата начала"
          onChange={onDateFrom}
          icon={<CalendarHeart className="h-5 w-5" />}
        />
        <DateField
          value={dateTo}
          ariaLabel="дата окончания"
          onChange={onDateTo}
          icon={<CalendarHeart className="h-5 w-5" />}
        />
      </div>
      <div className="tp-stats-action-row">
        <button
          className="tp-stats-filter-button"
          disabled={loading}
          onClick={onApply}
        >
          <RefreshCw className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
          <span>обновить</span>
        </button>
        <button
          className="tp-stats-filter-button"
          disabled={loading}
          onClick={onReset}
        >
          <Trash2 className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
          <span>сбросить</span>
        </button>
      </div>
    </div>
  );
}

function todayIso(): string {
  return moscowDateIso(new Date());
}

function moscowDateIso(value: Date): string {
  const parts = new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Europe/Moscow",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function defaultDateRange(): {
  dateFrom: string;
  dateTo: string;
} {
  const today = todayIso();
  return {
    dateFrom: today,
    dateTo: today,
  };
}

function acceptSnapshot(
  latestByKey: Map<string, Map<string, number>>,
  key: string,
  generatedAt: string | undefined,
  snapshotSource: Stats["snapshot_source"],
): boolean {
  const timestamp = generatedAt?.match(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.(\d{1,6}))?(?:Z|[+-]\d{2}:\d{2})$/,
  );
  const candidate =
    generatedAt && timestamp
      ? Date.parse(generatedAt) * 1000 +
        Number((timestamp[1] ?? "").padEnd(6, "0").slice(3))
      : Number.NaN;
  const source = snapshotSource ?? "legacy";
  if (
    !Number.isSafeInteger(candidate) ||
    !(["legacy", "direct", "projection"] as const).includes(source)
  ) {
    return false;
  }
  const latestBySource = latestByKey.get(key) ?? new Map<string, number>();
  const latest = latestBySource.get(source);
  if (latest !== undefined && candidate < latest) {
    return false;
  }
  latestBySource.set(source, candidate);
  latestByKey.set(key, latestBySource);
  return true;
}
